import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bus as BusIcon, MapPin, Calendar, AlertTriangle, CheckCircle, Plus, X, Wrench, FileText, Eye, Search, Edit2, Trash2, Printer, Package, ShoppingCart as ShoppingCartIcon, CreditCard } from 'lucide-react';
import { busesApi, mantenimientosApi, mecanicosApi, ventasApi } from '../lib/api';
import { toast } from 'sonner';
import BusDetalle from './BusDetalle';
import CarritoMantenimientoModal from '../components/CarritoMantenimientoModal';
import TicketImprimible from '../components/TicketImprimible';
import HistorialMantenimientosTicket from '../components/HistorialMantenimientosTicket';
import PagarCreditoModal from '../components/PagarCreditoModal';
import RankingReparacionesTicket from '../components/RankingReparacionesTicket';
import { rankingReparacionesApi } from '../lib/api';
import { fechaLima } from '../lib/fechas';
import { cerrarAlTocarFondo } from '../lib/tocarFondo';

// Definir tipo Bus
interface BusType {
  id: string;
  padron?: string;
  numero?: string;
  placa: string;
  marca: string;
  modelo: string;
  año?: number;
  kilometraje_actual: number;
  kilometraje?: number;
  capacidad?: number;
  estado: string;
  fecha_compra?: string;
  conductor_principal?: string;
  ultimo_mantenimiento?: string;
  monto_mantenimiento?: number | null;
  combustible: string;
  chasis: string;
  motor: string;
  fecha_creacion?: string;
  proximo_servicio_km?: number;
  servicios_completados?: number;
  ranking_score?: number;
  dueno?: string;
  telefono?: string;
  credito_usado?: number;
  credito_usado_productos?: number;
  credito_usado_servicios?: number;
}

const BusesCompleto = () => {
  const navigate = useNavigate();
  const [buses, setBuses] = useState<BusType[]>([]);
  const [mantenimientos, setMantenimientos] = useState<any[]>([]);
  const [mecanicos, setMecanicos] = useState<any[]>([]);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [editandoBus, setEditandoBus] = useState<BusType | null>(null);
  const [configuracionMantenimiento, setConfiguracionMantenimiento] = useState({
    diasVencimiento: 30, // Días antes del vencimiento para alertar
    kilometrajeMaximo: 10000, // Kilometraje máximo entre mantenimientos
    diasMaximoSinMantenimiento: 1 // TEMPORAL: 1 día para testing inmediato
  });
  const [mostrarDetalleMantenimiento, setMostrarDetalleMantenimiento] = useState(false);
  const [mantenimientoSeleccionado, setMantenimientoSeleccionado] = useState<any>(null);
  const [busSeleccionado, setBusSeleccionado] = useState<BusType | null>(null);
  const [rankingReparaciones, setRankingReparaciones] = useState<any>(null);
  const [imprimirRanking, setImprimirRanking] = useState<any>(null);
  const [mostrarHistorial, setMostrarHistorial] = useState(false);
  const [busDetalleId, setBusDetalleId] = useState<string | null>(null);
  const [mostrarServiciosUsados, setMostrarServiciosUsados] = useState(false);
  const [busServicios, setBusServicios] = useState<BusType | null>(null);
  const [ventasServicios, setVentasServicios] = useState<any[]>([]);
  const [cargandoVentasServicios, setCargandoVentasServicios] = useState(false);
  const [mostrarCarritoServicios, setMostrarCarritoServicios] = useState(false);
  const [historialCompras, setHistorialCompras] = useState<any[]>([]);
  const [mostrarHistorialCompras, setMostrarHistorialCompras] = useState(false);
  const [colaImpresion, setColaImpresion] = useState<any[]>([]);
  const [mostrarSelectorHistorial, setMostrarSelectorHistorial] = useState(false);
  const [busHistorialTermico, setBusHistorialTermico] = useState<BusType | null>(null);
  const [busParaPagarCredito, setBusParaPagarCredito] = useState<BusType | null>(null);
  const [fechaDesdeHistorial, setFechaDesdeHistorial] = useState('');
  const [fechaHastaHistorial, setFechaHastaHistorial] = useState('');
  const [historialParaImprimir, setHistorialParaImprimir] = useState<{ bus: BusType; mantenimientos: any[]; desde: string; hasta: string } | null>(null);
  const [nuevoBus, setNuevoBus] = useState({
    numero: '',
    placa: '',
    marca: '',
    modelo: '',
    combustible: '',
    estado: 'activo',
    dueno: '',
    telefono: '',
    monto_mantenimiento: ''
  });
  // Clave de administrador, solo se pide si realmente se cambia el monto de
  // mantenimiento (igual que anular un pago o un credito -- es una correccion sensible).
  const [claveMontoMantenimiento, setClaveMontoMantenimiento] = useState('');
  const cargarBuses = async () => {
    try {
      const data = await busesApi.getAll();
      setBuses(data || []);
    } catch (error) {
      console.error('Error cargando buses:', error);
    }
  };

  const cargarMantenimientos = async () => {
    try {
      const data = await mantenimientosApi.getAll();
      setMantenimientos(data || []);
    } catch (error) {
      console.error('Error cargando mantenimientos:', error);
    }
  };

  const cargarMecanicos = async () => {
    try {
      const data = await mecanicosApi.getAll();
      setMecanicos(data || []);
    } catch (err) {
      console.error('Error cargando mecánicos:', err);
    }
  };

  useEffect(() => {
    cargarBuses();
    cargarMantenimientos();
    cargarMecanicos();

    // Sincronización periódica (ya no hay suscripción realtime de Supabase)
    const intervalo = setInterval(() => {
      cargarBuses();
      cargarMantenimientos();
    }, 30000);

    return () => clearInterval(intervalo);
  }, []);

  // Cargar configuración de mantenimiento
  useEffect(() => {
    try {
      const configGuardada = localStorage.getItem('configuracionMantenimiento');
      if (configGuardada) {
        setConfiguracionMantenimiento(JSON.parse(configGuardada));
      }
    } catch (error) {
      console.error('Error cargando configuración de mantenimiento:', error);
    }
  }, []);

  // Debug: Log cuando cambien los buses
  useEffect(() => {
    console.log('🔄 Buses cambiaron. Total:', buses.length);
    console.log('📝 IDs de buses:', buses.map(b => `${b.placa} (${b.id})`));
  }, [buses]);

  // Filtrar buses basado en la búsqueda exacta del padrón
  const busesFiltrados = useMemo(() => {
    if (!busqueda.trim()) return buses;
    
    const termino = busqueda.toLowerCase();
    return buses.filter(bus => 
      bus.padron?.toLowerCase() === termino ||
      bus.numero?.toLowerCase() === termino
    );
  }, [buses, busqueda]);

  const guardarBuses = async (nuevosBuses: BusType[]) => {
    console.log('💾 Guardando buses a Supabase:', nuevosBuses.length);
    setBuses(nuevosBuses);
    
    // Real-time subscription will handle updates automatically
    // No need to manually emit events
  };

  // Funciones para alertas de mantenimiento
  const calcularDiasDesdeMantenimiento = (fechaUltimoMantenimiento?: string, fechaCreacion?: string): number => {
    if (!fechaUltimoMantenimiento) {
      // Si no hay fecha de último mantenimiento, calcular desde la fecha de creación o compra
      const fechaReferencia = fechaCreacion || fechaLima(new Date());
      const diferencia = new Date().getTime() - new Date(fechaReferencia).getTime();
      return Math.floor(diferencia / (1000 * 60 * 60 * 24));
    }
    
    const diferencia = new Date().getTime() - new Date(fechaUltimoMantenimiento).getTime();
    return Math.floor(diferencia / (1000 * 60 * 60 * 24));
  };  // Usar useMemo para recalcular alertas cuando cambien buses, mantenimientos o configuración
  const alertasMantenimiento = useMemo(() => {
    console.log('🔄 Recalculando alertas - Buses:', buses.length, 'Mantenimientos:', mantenimientos.length, 'Config:', configuracionMantenimiento.diasMaximoSinMantenimiento);
    
    const alertas: any[] = [];
    
    buses.forEach(bus => {
      // Encontrar el último mantenimiento completado de este bus
      const mantsBus = mantenimientos
        .filter(m => m.bus_id === bus.id && m.estado === 'completado')
        .sort((a, b) => new Date(b.fecha_programada).getTime() - new Date(a.fecha_programada).getTime());
      
      const ultimoMant = mantsBus[0];
      const fechaUltimoMant = ultimoMant?.fecha_programada || bus.ultimo_mantenimiento;
      
      const diasDesdeMantenimiento = calcularDiasDesdeMantenimiento(
        fechaUltimoMant, 
        bus.fecha_creacion || bus.fecha_compra
      );
      
      console.log(`📊 Bus ${bus.placa}: ${diasDesdeMantenimiento} días desde último mantenimiento, límite: ${configuracionMantenimiento.diasMaximoSinMantenimiento}`);
      
      // Alerta por tiempo transcurrido
      if (diasDesdeMantenimiento >= configuracionMantenimiento.diasMaximoSinMantenimiento) {
        console.log(`🔴 ALERTA CRÍTICA: ${bus.placa}`);
        alertas.push({
          id: `tiempo_${bus.id}`,
          busId: bus.id,
          bus: bus,
          tipo: 'critico',
          categoria: 'tiempo',
          titulo: fechaUltimoMant ? 'Mantenimiento Vencido' : 'Sin Mantenimiento Registrado',
          mensaje: `Padrón ${bus.padron} - ${bus.placa}: ${
            bus.ultimo_mantenimiento 
              ? `${diasDesdeMantenimiento} días sin mantenimiento`
              : `${diasDesdeMantenimiento} días sin mantenimiento (desde compra/creación)`
          }`,
          icono: 'AlertTriangle',
          color: 'red',
          prioridad: 1
        });
      } else if (diasDesdeMantenimiento >= (configuracionMantenimiento.diasMaximoSinMantenimiento - configuracionMantenimiento.diasVencimiento)) {
        console.log(`🟡 ALERTA ADVERTENCIA: ${bus.placa}`);
        alertas.push({
          id: `tiempo_${bus.id}`,
          busId: bus.id,
          bus: bus,
          tipo: 'advertencia',
          categoria: 'tiempo',
          titulo: 'Mantenimiento Próximo',
          mensaje: `Padrón ${bus.padron} - ${bus.placa}: ${diasDesdeMantenimiento} días sin mantenimiento`,
          icono: 'Clock',
          color: 'yellow',
          prioridad: 2
        });
      } else {
        console.log(`✅ Sin alerta: ${bus.placa} (${diasDesdeMantenimiento} días)`);
      }

      // Alerta por kilometraje
      if (bus.proximo_servicio_km && bus.kilometraje_actual >= bus.proximo_servicio_km) {
        alertas.push({
          id: `km_${bus.id}`,
          busId: bus.id,
          bus: bus,
          tipo: 'critico',
          categoria: 'kilometraje',
          titulo: 'Servicio por Kilometraje Vencido',
          mensaje: `Padrón ${bus.padron} - ${bus.placa}: ${bus.kilometraje_actual.toLocaleString()} km alcanzados`,
          icono: 'Tool',
          color: 'red',
          prioridad: 1
        });
      } else if (bus.proximo_servicio_km && (bus.proximo_servicio_km - bus.kilometraje_actual) <= 1000) {
        alertas.push({
          id: `km_${bus.id}`,
          busId: bus.id,
          bus: bus,
          tipo: 'advertencia',
          categoria: 'kilometraje',
          titulo: 'Próximo Servicio por Kilometraje',
          mensaje: `Padrón ${bus.padron} - ${bus.placa}: Faltan ${(bus.proximo_servicio_km - bus.kilometraje_actual).toLocaleString()} km`,
          icono: 'Wrench',
          color: 'yellow',
          prioridad: 2
        });
      }
    });

    console.log(`🎯 Total alertas generadas: ${alertas.length}`);
    console.log('📋 Alertas:', alertas.map(a => `${a.bus.placa} (${a.tipo})`));

    return alertas.sort((a, b) => a.prioridad - b.prioridad);
  }, [buses, mantenimientos, configuracionMantenimiento]); // Agregado mantenimientos

  // Helper para obtener alertas de un bus específico
  const obtenerAlertasBus = (busId: string) => {
    return alertasMantenimiento.filter(a => a.busId === busId);
  };

  // Mantenimientos de un bus, mas recientes primero
  const obtenerMantenimientosBus = (busId: string) => {
    return mantenimientos
      .filter(m => m.bus_id === busId)
      .sort((a, b) => new Date(b.fecha_programada).getTime() - new Date(a.fecha_programada).getTime());
  };

  const verHistorialBus = (bus: BusType) => {
    setBusSeleccionado(bus);
    setMostrarHistorial(true);
    setRankingReparaciones(null);
    const padron = bus.padron || bus.numero;
    if (padron) {
      rankingReparacionesApi.get(padron).then(setRankingReparaciones).catch(() => setRankingReparaciones(null));
    }
  };

  const imprimirRankingReparaciones = () => {
    if (!busSeleccionado || !rankingReparaciones) return;
    setImprimirRanking({ bus: busSeleccionado, ...rankingReparaciones });
  };

  const verDetallesMantenimiento = (mant: any) => {
    setMantenimientoSeleccionado(mant);
    setMostrarDetalleMantenimiento(true);
  };

  // "Piezas Usadas" ahora se basa solo en el Bus/Padron de la venta, no en si
  // hay un mantenimiento formal en curso.
  const abrirServiciosUsados = async (bus: BusType) => {
    setBusServicios(bus);
    setVentasServicios([]);
    setHistorialCompras([]);
    setMostrarHistorialCompras(false);
    setMostrarServiciosUsados(true);
    if (!bus.padron) return;
    setCargandoVentasServicios(true);
    try {
      const data = await ventasApi.getAbiertasPorBus(bus.padron);
      setVentasServicios(data || []);
    } catch (error) {
      console.error('Error cargando piezas pendientes:', error);
      toast.error('Error al cargar las piezas pendientes');
    } finally {
      setCargandoVentasServicios(false);
    }
  };

  const recargarVentasServicios = async () => {
    if (!busServicios?.padron) return;
    try {
      const data = await ventasApi.getAbiertasPorBus(busServicios.padron);
      setVentasServicios(data || []);
    } catch (error) {
      console.error('Error recargando piezas pendientes:', error);
    }
  };

  const cargarHistorialCompras = async () => {
    if (!busServicios?.padron) return;
    try {
      const data = await ventasApi.getHistorialPorBus(busServicios.padron);
      setHistorialCompras(data || []);
      setMostrarHistorialCompras(true);
    } catch (error) {
      console.error('Error cargando historial de compras:', error);
      toast.error('Error al cargar el historial');
    }
  };

  const cerrarEImprimirServicios = async () => {
    if (!busServicios?.padron || ventasServicios.length === 0) {
      toast.error('No hay piezas pendientes para cerrar');
      return;
    }
    try {
      const cerradas = await ventasApi.cerrarVentasBus(busServicios.padron);
      toast.success(`${cerradas.length} pieza(s) cerrada(s), imprimiendo...`);
      setVentasServicios([]);
      setColaImpresion(cerradas);
    } catch (error: any) {
      console.error('Error cerrando piezas pendientes:', error);
      toast.error('Error al cerrar: ' + error.message);
    }
  };

  // Imprime la cola de tickets uno por uno (cada uno con su propio corte de papel)
  useEffect(() => {
    if (colaImpresion.length > 0) {
      const timer = setTimeout(() => {
        window.print();
        setTimeout(() => {
          setColaImpresion(prev => prev.slice(1));
        }, 1200);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [colaImpresion]);

  const abrirSelectorHistorial = (bus: BusType) => {
    setBusHistorialTermico(bus);
    setFechaDesdeHistorial('');
    setFechaHastaHistorial('');
    setMostrarSelectorHistorial(true);
  };

  const imprimirHistorialTermico = () => {
    if (!busHistorialTermico || !fechaDesdeHistorial || !fechaHastaHistorial) {
      toast.error('Selecciona la fecha de inicio y fin');
      return;
    }
    const desde = new Date(fechaDesdeHistorial);
    const hasta = new Date(fechaHastaHistorial);
    hasta.setHours(23, 59, 59, 999);
    const mantenimientosBus = mantenimientos
      .filter(m => m.bus_id === busHistorialTermico.id && m.fecha_programada)
      .filter(m => {
        const f = new Date(m.fecha_programada);
        return f >= desde && f <= hasta;
      })
      .sort((a, b) => new Date(a.fecha_programada).getTime() - new Date(b.fecha_programada).getTime());

    setHistorialParaImprimir({
      bus: busHistorialTermico,
      mantenimientos: mantenimientosBus,
      desde: fechaDesdeHistorial,
      hasta: fechaHastaHistorial
    });
    setMostrarSelectorHistorial(false);
  };

  useEffect(() => {
    if (historialParaImprimir) {
      const timer = setTimeout(() => {
        window.print();
        setHistorialParaImprimir(null);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [historialParaImprimir]);

  useEffect(() => {
    if (imprimirRanking) {
      const timer = setTimeout(() => {
        window.print();
        setImprimirRanking(null);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [imprimirRanking]);

  // Funciones de edición
  const editarBus = (bus: BusType) => {
    setEditandoBus(bus);
    setNuevoBus({
      numero: bus.padron || bus.numero || '',
      placa: bus.placa || '',
      marca: bus.marca || '',
      modelo: bus.modelo || '',
      combustible: bus.combustible || '',
      estado: bus.estado || 'activo',
      dueno: bus.dueno || '',
      telefono: bus.telefono || '',
      monto_mantenimiento: bus.monto_mantenimiento != null ? String(bus.monto_mantenimiento) : ''
    });
    setClaveMontoMantenimiento('');
    setMostrarFormulario(true);
  };

  const guardarEdicionBus = async () => {
    if (!editandoBus) return;
    
    if (!nuevoBus.numero || !nuevoBus.placa || !nuevoBus.marca || !nuevoBus.modelo) {
      alert('Por favor complete todos los campos obligatorios');
      return;
    }

    // Validar placa solo si cambió
    const placaOriginal = editandoBus.placa?.toUpperCase();
    const placaNueva = nuevoBus.placa.toUpperCase();

    if (placaOriginal !== placaNueva) {
      const busExistente = buses.find(b => b.id !== editandoBus.id && b.placa?.toUpperCase() === placaNueva);
      if (busExistente) {
        toast.error('Ya existe un bus con esa placa');
        return;
      }
    }

    // El monto de mantenimiento es una excepcion sensible (afecta lo que se cobra cada
    // quincena): solo se pide la clave de administrador si realmente se esta cambiando,
    // no en cada edicion del bus.
    const montoMantOriginal = editandoBus.monto_mantenimiento != null ? String(editandoBus.monto_mantenimiento) : '';
    const montoMantNuevo = nuevoBus.monto_mantenimiento.trim();
    const cambioMontoMantenimiento = montoMantOriginal !== montoMantNuevo;

    if (cambioMontoMantenimiento && !claveMontoMantenimiento.trim()) {
      toast.error('Ingresa la clave de administrador para cambiar el monto de mantenimiento');
      return;
    }

    try {
      const busData: Record<string, any> = {
        padron: nuevoBus.numero,
        placa: placaNueva,
        marca: nuevoBus.marca,
        modelo: nuevoBus.modelo,
        combustible: nuevoBus.combustible,
        estado: nuevoBus.estado,
        dueno: nuevoBus.dueno || null,
        telefono: nuevoBus.telefono || null,
      };
      if (cambioMontoMantenimiento) {
        busData.monto_mantenimiento = montoMantNuevo === '' ? null : parseFloat(montoMantNuevo);
        busData.clave = claveMontoMantenimiento.trim();
      }

      await busesApi.update(editandoBus.id, busData);
      await cargarBuses();

      toast.success('Bus actualizado exitosamente');
      setClaveMontoMantenimiento('');
      cancelarEdicion();
    } catch (error: any) {
      console.error('Error editando bus:', error);
      toast.error('Error al editar bus: ' + error.message);
    }
  };

  const eliminarBus = async (bus: BusType) => {
    if (!confirm(`¿Estás seguro de eliminar el bus ${bus.placa}?`)) return;

    try {
      await busesApi.delete(bus.id);
      await cargarBuses();
      toast.success('Bus eliminado exitosamente');
    } catch (error: any) {
      console.error('Error eliminando bus:', error);
      toast.error('Error al eliminar bus: ' + error.message);
    }
  };

  const cancelarEdicion = () => {
    setEditandoBus(null);
    setNuevoBus({
      numero: '',
      placa: '',
      marca: '',
      modelo: '',
      combustible: '',
      estado: 'activo',
      dueno: '',
      telefono: '',
      monto_mantenimiento: ''
    });
    setClaveMontoMantenimiento('');
    setMostrarFormulario(false);
  };

  const agregarBus = async () => {
    if (!nuevoBus.numero || !nuevoBus.placa || !nuevoBus.marca || !nuevoBus.modelo) {
      alert('Por favor complete todos los campos obligatorios');
      return;
    }

    // Verificar que la placa no exista
    if (buses.some(b => b.placa.toLowerCase() === nuevoBus.placa.toLowerCase())) {
      alert('Ya existe un bus con esa placa');
      return;
    }

    // Verificar que el padrón no exista
    if (buses.some(b => b.padron === nuevoBus.numero)) {
      alert('Ya existe un bus con ese padrón');
      return;
    }

    const bus = {
      padron: nuevoBus.numero,
      placa: nuevoBus.placa,
      marca: nuevoBus.marca,
      modelo: nuevoBus.modelo,
      año: new Date().getFullYear(),
      kilometraje: 0,
      estado: nuevoBus.estado || 'activo',
      dueno: nuevoBus.dueno || null,
      telefono: nuevoBus.telefono || null
    };

    let busCreado: any;
    try {
      busCreado = await busesApi.create(bus);
      await cargarBuses();
      console.log('✅ Bus agregado');
    } catch (error) {
      console.error('Error agregando bus:', error);
      alert('Error al agregar el bus: ' + (error as Error).message);
      return;
    }

    // Resetear el formulario
    setNuevoBus({
      numero: '',
      placa: '',
      marca: '',
      modelo: '',
      combustible: '',
      estado: 'activo',
      dueno: '',
      telefono: '',
      monto_mantenimiento: ''
    });
    setMostrarFormulario(false);
    
    // Mostrar mensaje de confirmación
    setTimeout(() => {
      const alertas = alertasMantenimiento.filter(a => a.busId === busCreado?.id);
      const mensajeAlertas = alertas.length > 0 
        ? `\n⚠️ Este bus tiene ${alertas.length} alerta(s) de mantenimiento activas.`
        : '\n✅ Este bus no requiere mantenimiento inmediato.';
      
      alert(`Bus agregado exitosamente: ${bus.placa}${mensajeAlertas}`);
    }, 100);
  };

  const cambiarEstado = (id: string) => {
    const nuevosBuses = buses.map(b => 
      b.id === id ? { 
        ...b, 
        estado: b.estado === 'activo' ? 'mantenimiento' : 'activo' 
      } : b
    );
    guardarBuses(nuevosBuses);
  };

  const busesActivos = buses.filter(b => b.estado === 'activo').length;
  const busesMantenimiento = buses.filter(b => b.estado === 'mantenimiento').length;
  const kilometrajeTotalFlota = buses.reduce((sum, b) => sum + b.kilometraje_actual, 0);
  const promedioKilometraje = buses.length > 0 ? Math.round(kilometrajeTotalFlota / buses.length) : 0;

  const generarReporteBus = async (busId: string) => {
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

      const bus = buses.find(b => b.id === busId);
      if (!bus) {
        alert('❌ Bus no encontrado');
        return;
      }

      const padron = bus.padron || bus.numero;
      console.log('🚌 Generando reporte TXT para bus:', padron);
      console.log('🔧 Mecánicos disponibles:', mecanicos.length, mecanicos);

      // Obtener mantenimientos del bus
      const mantenimientosBus = mantenimientos.filter(m => m.bus_id === busId);
      console.log('📋 Mantenimientos del bus:', mantenimientosBus.length);
      
      // Enriquecer con nombres de mecánicos
      const mantenimientosEnriquecidos = mantenimientosBus.map(mant => {
        const mecanico = mecanicos.find(m => m.id === mant.mecanico_id);
        let mecanicoNombre = null;
        
        console.log(`🔍 Procesando mantenimiento:`, {
          id: mant.id,
          mecanico_id: mant.mecanico_id,
          mecanico_encontrado: mecanico,
          nombre_mecanico: mecanicoNombre
        });
        
        if (mecanico) {
          mecanicoNombre = mecanico.apellido 
            ? `${mecanico.nombre} ${mecanico.apellido}`.trim()
            : mecanico.nombre;
        }
        
        return {
          ...mant,
          mecanico_nombre: mecanicoNombre
        };
      });

      console.log('📦 Enviando', mantenimientosEnriquecidos.length, 'mantenimientos enriquecidos');

      // Mostrar indicador de carga
      alert('⏳ Generando reporte, por favor espera...');

      // Usar endpoint TXT con padrón y mantenimientos enriquecidos
      const response = await fetch(`${API_URL}/api/reportes/bus-mantenimiento/${padron}/txt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          mantenimientos: mantenimientosEnriquecidos
        })
      }).catch(err => {
        console.error('Error de red:', err);
        throw new Error('Error de conexión con el servidor. Verifica que el API esté corriendo.');
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`Error del servidor: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('Resultado:', result);
      
      if (result.success) {
        alert(`✅ Reporte generado y guardado\n\nArchivo: ${result.filename}\n\nPuedes verlo en: ${result.url || 'Google Drive'}`);
      } else {
        throw new Error(result.error || 'Error desconocido');
      }
      
    } catch (error: any) {
      console.error('Error generando reporte:', error);
      alert('❌ Error al generar el reporte: ' + error.message);
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Gestión de Buses</h1>
        <div className="flex space-x-2">
          <button
            onClick={() => setMostrarFormulario(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            <span>Agregar Bus</span>
          </button>
        </div>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <BusIcon className="h-8 w-8 text-blue-600 mr-3" />
            <div>
              <p className="text-sm text-gray-600">Total Buses</p>
              <p className="text-2xl font-bold">{buses.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <CheckCircle className="h-8 w-8 text-green-600 mr-3" />
            <div>
              <p className="text-sm text-gray-600">Activos</p>
              <p className="text-2xl font-bold">{busesActivos}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <Wrench className="h-8 w-8 text-orange-600 mr-3" />
            <div>
              <p className="text-sm text-gray-600">En Mantenimiento</p>
              <p className="text-2xl font-bold">{busesMantenimiento}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <MapPin className="h-8 w-8 text-purple-600 mr-3" />
            <div>
              <p className="text-sm text-gray-600">Km Promedio</p>
              <p className="text-2xl font-bold">{promedioKilometraje.toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Barra de búsqueda */}
      <div className="bg-white rounded-lg shadow mb-6 p-4">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar buses por padrón, placa, marca o modelo..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          {busqueda && (
            <button
              onClick={() => setBusqueda('')}
              className="px-3 py-2 text-gray-500 hover:text-gray-700"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      
      {/* Formulario para agregar/editar bus (popup, no se pierde con el scroll) */}
      {mostrarFormulario && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        >
        <div className="bg-white rounded-lg shadow w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">
                {editandoBus ? 'Editar Bus' : 'Agregar Nuevo Bus'}
              </h2>
              <button
                onClick={() => setMostrarFormulario(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Padrón *
                </label>
                <input
                  type="text"
                  value={nuevoBus.numero}
                  onChange={(e) => setNuevoBus({...nuevoBus, numero: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="Ej: 001"
                  disabled={editandoBus !== null}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Placa *
                </label>
                <input
                  type="text"
                  value={nuevoBus.placa}
                  onChange={(e) => setNuevoBus({...nuevoBus, placa: e.target.value.toUpperCase()})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="Ej: ABC-123"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Marca *
                </label>
                <select
                  value={nuevoBus.marca}
                  onChange={(e) => setNuevoBus({...nuevoBus, marca: e.target.value, modelo: ''})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="">Seleccionar marca</option>
                  <option value="Volkswagen">Volkswagen</option>
                  <option value="Mercedes Benz">Mercedes Benz</option>
                  <option value="Fiat">Fiat</option>
                  <option value="Cummins">Cummins</option>
                  <option value="Iveco">Iveco</option>
                  <option value="MODASA">MODASA</option>
                  <option value="Otros">Otros</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Modelo *
                </label>
                <select
                  value={nuevoBus.modelo}
                  onChange={(e) => setNuevoBus({...nuevoBus, modelo: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  disabled={!nuevoBus.marca}
                >
                  <option value="">
                    {nuevoBus.marca ? 'Seleccionar modelo' : 'Primero selecciona una marca'}
                  </option>
                  {nuevoBus.marca === 'Mercedes Benz' && (
                    <>
                      <option value="MB 1722">MB 1722</option>
                      <option value="MB 1721">MB 1721</option>
                      <option value="MB-Weichai">MB-Weichai</option>
                      <option value="Otros">Otros</option>
                    </>
                  )}
                  {nuevoBus.marca === 'Volkswagen' && (
                    <>
                      <option value="VW 17210">VW 17210</option>
                      <option value="Otros">Otros</option>
                    </>
                  )}
                  {nuevoBus.marca === 'Cummins' && (
                    <>
                      <option value="Cummins Bgas Plus">Cummins Bgas Plus</option>
                      <option value="Otros">Otros</option>
                    </>
                  )}
                  {nuevoBus.marca === 'Fiat' && (
                    <>
                      <option value="Fiat FPT">Fiat FPT</option>
                      <option value="Otros">Otros</option>
                    </>
                  )}
                  {nuevoBus.marca === 'Iveco' && (
                    <>
                      <option value="Iveco Tector">Iveco Tector</option>
                      <option value="Otros">Otros</option>
                    </>
                  )}
                  {nuevoBus.marca === 'MODASA' && (
                    <>
                      <option value="Motug">Motug</option>
                      <option value="Otros">Otros</option>
                    </>
                  )}
                  {nuevoBus.marca === 'Otros' && (
                    <>
                      <option value="Otros">Otros</option>
                    </>
                  )}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de Combustible *
                </label>
                <select
                  value={nuevoBus.combustible || ''}
                  onChange={(e) => setNuevoBus({...nuevoBus, combustible: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="">Seleccionar combustible</option>
                  <option value="Gasolina">Gasolina</option>
                  <option value="Diesel">Diesel</option>
                  <option value="GLP">GLP (Gas Licuado de Petróleo)</option>
                  <option value="GNV">GNV (Gas Natural Vehicular)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Dueño del Bus
                </label>
                <input
                  type="text"
                  value={nuevoBus.dueno}
                  onChange={(e) => setNuevoBus({...nuevoBus, dueno: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="Nombre del propietario"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Teléfono
                </label>
                <input
                  type="tel"
                  value={nuevoBus.telefono}
                  onChange={(e) => setNuevoBus({...nuevoBus, telefono: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="999 999 999"
                />
              </div>
              {editandoBus && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Monto de mantenimiento propio (S/)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={nuevoBus.monto_mantenimiento}
                    onChange={(e) => setNuevoBus({...nuevoBus, monto_mantenimiento: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    placeholder="Dejar vacío para usar el monto general"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Excepción para este bus (ej. S/200 en vez del monto general). Vacío = usa el monto general de Pago de Mantenimiento.
                  </p>
                  {nuevoBus.monto_mantenimiento.trim() !== (editandoBus.monto_mantenimiento != null ? String(editandoBus.monto_mantenimiento) : '') && (
                    <div className="mt-2 bg-red-50 border border-red-200 rounded-lg p-2">
                      <label className="block text-xs font-medium text-red-700 mb-1">
                        Clave de administrador (obligatoria para cambiar este monto)
                      </label>
                      <input
                        type="password"
                        value={claveMontoMantenimiento}
                        onChange={(e) => setClaveMontoMantenimiento(e.target.value)}
                        className="w-full border border-red-300 rounded-lg px-3 py-1.5 text-sm"
                        placeholder="****"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setMostrarFormulario(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={editandoBus ? guardarEdicionBus : agregarBus}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                {editandoBus ? 'Guardar Cambios' : 'Agregar Bus'}
              </button>
            </div>
          </div>
        </div>
        </div>
      )}
      
      {/* Lista de buses */}
      <div className="grid gap-6">
        {busesFiltrados.map((bus) => (
          <div key={bus.id} className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center space-x-4">
                <BusIcon className="h-12 w-12 text-blue-600" />
                <div>
                  <h2 className="text-5xl font-bold text-blue-600">{bus.padron || bus.numero}</h2>
                  <p className="text-gray-500 text-sm mt-1">{bus.placa}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {/* Botón Ver Detalle */}
                <button
                  onClick={() => setBusDetalleId(bus.id)}
                  className="px-3 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700 flex items-center"
                  title="Ver detalle y categorías de mantenimiento"
                >
                  <Eye className="w-3 h-3 mr-1" />
                  Ver Detalle
                </button>
                {/* Botón Editar */}
                <button
                  onClick={() => editarBus(bus)}
                  className="px-2 py-1 bg-yellow-600 text-white text-xs rounded hover:bg-yellow-700 flex items-center"
                  title="Editar bus"
                >
                  <Edit2 className="w-3 h-3 mr-1" />
                  Editar
                </button>
                {/* Botón Eliminar */}
                <button
                  onClick={() => eliminarBus(bus)}
                  className="px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 flex items-center"
                  title="Eliminar bus"
                >
                  <Trash2 className="w-3 h-3 mr-1" />
                  Eliminar
                </button>
                {/* Botón reporte del bus */}
                <button
                  onClick={() => generarReporteBus(bus.id)}
                  className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 flex items-center"
                  title="Generar reporte de este bus"
                >
                  <FileText className="w-3 h-3 mr-1" />
                  Reporte
                </button>
                {/* Botón Ver Historial */}
                <button
                  onClick={() => verHistorialBus(bus)}
                  className="px-2 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700 flex items-center"
                  title="Ver historial de mantenimientos"
                >
                  <Eye className="w-3 h-3 mr-1" />
                  Historial
                </button>
                {/* Botón Piezas Usadas (compras de repuestos/insumos de este bus) */}
                <button
                  onClick={() => abrirServiciosUsados(bus)}
                  className="px-2 py-1 bg-teal-600 text-white text-xs rounded hover:bg-teal-700 flex items-center"
                  title="Ver piezas pendientes y el historial de compras de este bus"
                >
                  <Package className="w-3 h-3 mr-1" />
                  Piezas Usadas
                </button>
                {/* Botón Crédito del bus (ver deuda y registrar pago parcial/total) */}
                <button
                  onClick={() => setBusParaPagarCredito(bus)}
                  className={`px-2 py-1 text-xs rounded flex items-center ${
                    (bus.credito_usado || 0) > 0
                      ? 'bg-red-600 hover:bg-red-700 text-white'
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                  }`}
                  title="Ver y pagar el crédito de este bus"
                >
                  <CreditCard className="w-3 h-3 mr-1" />
                  Crédito: S/ {(bus.credito_usado || 0).toFixed(2)}
                </button>
                <button
                  onClick={() => cambiarEstado(bus.id)}
                  className={`px-3 py-1 rounded-full text-sm font-medium cursor-pointer ${
                    bus.estado === 'activo' 
                      ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                      : 'bg-red-100 text-red-800 hover:bg-red-200'
                  }`}
                >
                  {bus.estado === 'activo' ? (
                    <><CheckCircle className="w-4 h-4 inline mr-1" />Activo</>
                  ) : (
                    <><AlertTriangle className="w-4 h-4 inline mr-1" />Mantenimiento</>
                  )}
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 p-3 rounded">
                <p className="text-sm text-gray-600">Kilometraje Actual</p>
                <p className="font-bold">{(bus.kilometraje || bus.kilometraje_actual || 0).toLocaleString()} km</p>
              </div>
              <div className="bg-gray-50 p-3 rounded">
                <p className="text-sm text-gray-600">Padrón</p>
                <p className="font-bold">{bus.padron || bus.numero}</p>
              </div>
              <div className={`p-3 rounded ${
                calcularDiasDesdeMantenimiento(bus.ultimo_mantenimiento, bus.fecha_creacion || bus.fecha_compra) >= configuracionMantenimiento.diasMaximoSinMantenimiento - configuracionMantenimiento.diasVencimiento
                  ? 'bg-red-50 border border-red-200' 
                  : 'bg-gray-50'
              }`}>
                <p className="text-sm text-gray-600">Último Mantenimiento</p>
                <p className="font-bold">{bus.ultimo_mantenimiento || 'Sin registrar'}</p>
                {(() => {
                  const dias = calcularDiasDesdeMantenimiento(bus.ultimo_mantenimiento, bus.fecha_creacion || bus.fecha_compra);
                  return (
                    <p className={`text-xs ${
                      dias >= configuracionMantenimiento.diasMaximoSinMantenimiento - configuracionMantenimiento.diasVencimiento
                        ? 'text-red-600' 
                        : 'text-gray-500'
                    }`}>
                      {bus.ultimo_mantenimiento 
                        ? `Hace ${dias} días`
                        : `Sin mantenimiento (${dias} días desde compra/creación)`
                      }
                    </p>
                  );
                })()}
              </div>
            </div>
            
            {/* Información adicional de mantenimiento */}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className={`p-3 rounded border ${
                bus.proximo_servicio_km && bus.kilometraje_actual >= bus.proximo_servicio_km
                  ? 'bg-red-50 border-red-200'
                  : bus.proximo_servicio_km && (bus.proximo_servicio_km - bus.kilometraje_actual) <= 1000
                  ? 'bg-yellow-50 border-yellow-200'
                  : 'bg-gray-50 border-gray-200'
              }`}>
                <p className="text-sm text-gray-600 flex items-center">
                  <Wrench className="w-4 h-4 mr-1" />
                  Próximo Servicio por Kilometraje
                </p>
                <p className="font-bold">
                  {bus.proximo_servicio_km ? `${bus.proximo_servicio_km.toLocaleString()} km` : 'No programado'}
                </p>
                {bus.proximo_servicio_km && (
                  <p className={`text-xs ${
                    bus.kilometraje_actual >= bus.proximo_servicio_km
                      ? 'text-red-600 font-medium'
                      : (bus.proximo_servicio_km - bus.kilometraje_actual) <= 1000
                      ? 'text-yellow-600 font-medium'
                      : 'text-gray-500'
                  }`}>
                    {bus.kilometraje_actual >= bus.proximo_servicio_km
                      ? `¡Vencido! (+${(bus.kilometraje_actual - bus.proximo_servicio_km).toLocaleString()} km)`
                      : `Faltan ${(bus.proximo_servicio_km - bus.kilometraje_actual).toLocaleString()} km`
                    }
                  </p>
                )}
              </div>
              
              <div className="bg-blue-50 border border-blue-200 p-3 rounded">
                <p className="text-sm text-gray-600 flex items-center">
                  <Calendar className="w-4 h-4 mr-1" />
                  Estado de Mantenimiento
                </p>
                <p className="font-bold">
                  {obtenerAlertasBus(bus.id).some(a => a.tipo === 'critico')
                    ? 'Mantenimiento Urgente'
                    : obtenerAlertasBus(bus.id).length > 0
                    ? 'Mantenimiento Próximo'
                    : 'Al día'
                  }
                </p>
                <p className="text-xs text-blue-600">
                  {obtenerAlertasBus(bus.id).length > 0
                    ? `${obtenerAlertasBus(bus.id).length} alerta(s) activa(s)`
                    : 'Sin alertas de mantenimiento'
                  }
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal: Historial de Mantenimientos del Bus */}
      {mostrarHistorial && busSeleccionado && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-6 border-b flex justify-between items-center bg-gray-50">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">Historial de Mantenimientos</h2>
                <p className="text-gray-600 mt-1">
                  Bus {busSeleccionado.padron || busSeleccionado.numero} - {busSeleccionado.placa}
                </p>
              </div>
              <button
                onClick={() => {
                  setMostrarHistorial(false);
                  setBusSeleccionado(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Ranking de reparaciones por categoria */}
            {rankingReparaciones && (
              <div className="px-6 pt-4">
                {rankingReparaciones.alerta_activa && (
                  <div className="mb-3 p-3 bg-red-50 border border-red-300 rounded-lg flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-red-800">
                      <p className="font-semibold">Fallas frecuentes: {rankingReparaciones.reparaciones_ultimos_15_dias} reparaciones en los últimos 15 días</p>
                      {rankingReparaciones.categoria_mas_frecuente_reciente && (
                        <p>Categoría más frecuente: <span className="capitalize">{rankingReparaciones.categoria_mas_frecuente_reciente.replace(/_/g, ' ')}</span></p>
                      )}
                    </div>
                  </div>
                )}
                {rankingReparaciones.ranking.length > 0 && (
                  <div className="mb-4 border rounded-lg p-3 bg-gray-50">
                    <div className="flex justify-between items-center mb-2">
                      <p className="text-sm font-semibold text-gray-700">Ranking de reparaciones por categoría</p>
                      <button
                        onClick={imprimirRankingReparaciones}
                        className="text-xs px-2 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700 flex items-center gap-1"
                      >
                        <Printer className="w-3 h-3" />
                        Imprimir Ranking
                      </button>
                    </div>
                    <div className="space-y-1">
                      {rankingReparaciones.ranking.map((r: any) => (
                        <div key={r.categoria} className="flex justify-between text-sm">
                          <span className="capitalize">{r.categoria.replace(/_/g, ' ')}</span>
                          <span className="font-medium">{r.cantidad}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Content */}
            <div className="p-6 overflow-y-auto flex-1 pt-2">
              {obtenerMantenimientosBus(busSeleccionado.id).length === 0 ? (
                <div className="text-center py-12">
                  <Wrench className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500 text-lg">Sin mantenimientos registrados</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {obtenerMantenimientosBus(busSeleccionado.id).map((mant, index) => {
                    const mecanico = mecanicos.find(m => m.id === mant.mecanico_id);
                    return (
                      <div key={mant.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`px-3 py-1 rounded text-sm font-medium ${
                                mant.estado === 'completado' ? 'bg-green-100 text-green-800' :
                                mant.estado === 'en_proceso' ? 'bg-yellow-100 text-yellow-800' :
                                mant.estado === 'pendiente' ? 'bg-blue-100 text-blue-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {mant.estado?.toUpperCase()}
                              </span>
                              <span className="text-sm text-gray-500">#{index + 1}</span>
                            </div>
                            <h3 className="font-semibold text-lg text-gray-800 capitalize">
                              {mant.tipo} - {mant.categoria?.replace(/_/g, ' ')}
                            </h3>
                          </div>
                          <button
                            onClick={() => verDetallesMantenimiento(mant)}
                            className="px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 flex items-center gap-2 transition-colors"
                          >
                            <Eye className="w-4 h-4" />
                            Ver Detalles
                          </button>
                        </div>

                        <p className="text-gray-700 mb-4">{mant.descripcion}</p>

                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div className="bg-gray-50 p-3 rounded">
                            <p className="text-gray-500 text-xs mb-1">📅 Fecha Programada</p>
                            <p className="font-medium">{new Date(mant.fecha_programada).toLocaleDateString('es-ES')}</p>
                          </div>
                          <div className={`p-3 rounded ${mant.fecha_atencion ? 'bg-green-50' : 'bg-gray-50'}`}>
                            <p className="text-gray-500 text-xs mb-1">🔧 Fecha Realizada</p>
                            <p className={`font-medium ${mant.fecha_atencion ? 'text-green-700' : 'text-gray-400'}`}>
                              {mant.fecha_atencion 
                                ? new Date(mant.fecha_atencion).toLocaleDateString('es-ES')
                                : 'Sin atender'}
                            </p>
                          </div>
                          <div className="bg-gray-50 p-3 rounded">
                            <p className="text-gray-500 text-xs mb-1">👤 Mecánico</p>
                            <p className="font-medium">{mecanico?.nombre || mant.mecanico_id || 'No asignado'}</p>
                          </div>
                          <div className="bg-gray-50 p-3 rounded">
                            <p className="text-gray-500 text-xs mb-1">⏰ Turno</p>
                            <p className="font-medium capitalize">{mant.turno || 'No especificado'}</p>
                          </div>
                        </div>

                        {mant.observaciones && (
                          <div className="mt-3 p-3 bg-yellow-50 rounded border border-yellow-200">
                            <p className="text-xs text-yellow-700 font-medium mb-1">📝 Observaciones</p>
                            <p className="text-sm text-yellow-800">{mant.observaciones}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t bg-gray-50 flex justify-between items-center">
              <p className="text-sm text-gray-600">
                Total: {obtenerMantenimientosBus(busSeleccionado.id).length} mantenimiento(s)
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => abrirSelectorHistorial(busSeleccionado)}
                  className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 flex items-center gap-2"
                  title="Imprimir este historial en la impresora térmica"
                >
                  <Printer className="w-4 h-4" />
                  Imprimir en Térmica
                </button>
                <button
                  onClick={() => {
                    setMostrarHistorial(false);
                    setBusSeleccionado(null);
                  }}
                  className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Detalles Completos del Mantenimiento */}
      {mostrarDetalleMantenimiento && mantenimientoSeleccionado && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-6 border-b bg-gradient-to-r from-blue-500 to-blue-600 text-white">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold">Detalles del Mantenimiento</h2>
                  <p className="text-blue-100 mt-1">Vista previa completa</p>
                </div>
                <button
                  onClick={() => {
                    setMostrarDetalleMantenimiento(false);
                    setMantenimientoSeleccionado(null);
                  }}
                  className="text-white hover:text-blue-100"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto flex-1">
              <div className="space-y-6">
                {/* Estado y Tipo */}
                <div className="flex gap-4">
                  <div className="flex-1 bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-500 mb-2">Estado</p>
                    <span className={`px-4 py-2 rounded-lg text-sm font-bold inline-block ${
                      mantenimientoSeleccionado.estado === 'completado' ? 'bg-green-100 text-green-800' :
                      mantenimientoSeleccionado.estado === 'en_proceso' ? 'bg-yellow-100 text-yellow-800' :
                      mantenimientoSeleccionado.estado === 'pendiente' ? 'bg-blue-100 text-blue-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {mantenimientoSeleccionado.estado?.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-500 mb-2">Tipo de Mantenimiento</p>
                    <p className="text-lg font-bold capitalize">{mantenimientoSeleccionado.tipo}</p>
                  </div>
                </div>

                {/* Categoría y Bus */}
                <div className="flex gap-4">
                  <div className="flex-1 bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-500 mb-2">Categoría</p>
                    <p className="text-lg font-semibold capitalize">
                      {mantenimientoSeleccionado.categoria?.replace(/_/g, ' ')}
                    </p>
                  </div>
                  <div className="flex-1 bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-500 mb-2">Bus</p>
                    <p className="text-lg font-semibold">
                      {buses.find(b => b.id === mantenimientoSeleccionado.bus_id)?.placa || 'Desconocido'}
                    </p>
                  </div>
                </div>

                {/* Descripción */}
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-700 font-medium mb-2">📋 Descripción</p>
                  <p className="text-gray-800">{mantenimientoSeleccionado.descripcion || 'Sin descripción'}</p>
                </div>

                {/* Fechas */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-blue-500">
                    <p className="text-sm text-gray-500 mb-2">📅 Fecha Programada</p>
                    <p className="text-lg font-bold">
                      {new Date(mantenimientoSeleccionado.fecha_programada).toLocaleDateString('es-ES', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                  <div className={`p-4 rounded-lg border-l-4 ${
                    mantenimientoSeleccionado.fecha_atencion 
                      ? 'bg-green-50 border-green-500' 
                      : 'bg-gray-50 border-gray-300'
                  }`}>
                    <p className="text-sm text-gray-500 mb-2">🔧 Fecha Realizada</p>
                    <p className={`text-lg font-bold ${
                      mantenimientoSeleccionado.fecha_atencion 
                        ? 'text-green-700' 
                        : 'text-gray-400'
                    }`}>
                      {mantenimientoSeleccionado.fecha_atencion
                        ? new Date(mantenimientoSeleccionado.fecha_atencion).toLocaleDateString('es-ES', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })
                        : 'Sin atender'}
                    </p>
                  </div>
                </div>

                {/* Mecánico y Turno */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                    <p className="text-sm text-purple-700 mb-2">👤 Mecánico Asignado</p>
                    <p className="text-lg font-semibold">
                      {mecanicos.find(m => m.id === mantenimientoSeleccionado.mecanico_id)?.nombre || 
                       mantenimientoSeleccionado.mecanico_id || 
                       'No asignado'}
                    </p>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                    <p className="text-sm text-orange-700 mb-2">⏰ Turno</p>
                    <p className="text-lg font-semibold capitalize">
                      {mantenimientoSeleccionado.turno || 'No especificado'}
                    </p>
                  </div>
                </div>

                {/* Kilometraje */}
                {mantenimientoSeleccionado.kilometraje_servicio && (
                  <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                    <p className="text-sm text-indigo-700 mb-2">📏 Kilometraje al Servicio</p>
                    <p className="text-lg font-bold">
                      {mantenimientoSeleccionado.kilometraje_servicio.toLocaleString()} km
                    </p>
                  </div>
                )}

                {/* Observaciones */}
                {mantenimientoSeleccionado.observaciones && (
                  <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-400">
                    <p className="text-sm text-yellow-700 font-medium mb-2">📝 Observaciones</p>
                    <p className="text-gray-800 whitespace-pre-wrap">
                      {mantenimientoSeleccionado.observaciones}
                    </p>
                  </div>
                )}

                {/* Costo (si existe) */}
                {mantenimientoSeleccionado.costo && (
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <p className="text-sm text-green-700 mb-2">💰 Costo</p>
                    <p className="text-2xl font-bold text-green-800">
                      S/ {parseFloat(mantenimientoSeleccionado.costo).toFixed(2)}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => {
                  setMostrarDetalleMantenimiento(false);
                  setMantenimientoSeleccionado(null);
                }}
                className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Cerrar
              </button>
              <button
                onClick={() => navigate(`/mantenimientos`)}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Ir a Mantenimientos
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Ver Detalle del Bus (popup, ya no navega a otra pagina) */}
      {busDetalleId && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          {...cerrarAlTocarFondo(() => setBusDetalleId(null))}
        >
          <div className="bg-white rounded-lg w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            <BusDetalle busIdProp={busDetalleId} onClose={() => setBusDetalleId(null)} />
          </div>
        </div>
      )}

      {/* Modal: Piezas Usadas del bus (pendientes por cerrar + historial permanente) */}
      {mostrarServiciosUsados && busServicios && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          {...cerrarAlTocarFondo(() => setMostrarServiciosUsados(false))}
        >
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center bg-gray-50">
              <div>
                <h2 className="text-xl font-bold text-gray-800">Piezas Usadas</h2>
                <p className="text-gray-600 mt-1">
                  Bus {busServicios.padron || busServicios.numero} - {busServicios.placa}
                </p>
              </div>
              <button onClick={() => setMostrarServiciosUsados(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => setMostrarCarritoServicios(true)}
                  className="flex-1 bg-teal-600 hover:bg-teal-700 text-white px-3 py-2 rounded-lg flex items-center justify-center gap-2 text-sm"
                >
                  <ShoppingCartIcon size={16} />
                  Agregar Piezas
                </button>
                <button
                  onClick={cerrarEImprimirServicios}
                  disabled={ventasServicios.length === 0}
                  className="flex-1 bg-gray-700 hover:bg-gray-800 text-white px-3 py-2 rounded-lg flex items-center justify-center gap-2 text-sm disabled:opacity-50"
                >
                  <Printer size={16} />
                  Cerrar e Imprimir
                </button>
              </div>

              <h3 className="font-semibold text-gray-700 mb-2">Pendientes por cerrar</h3>
              {cargandoVentasServicios ? (
                <p className="text-center text-gray-500 py-6">Cargando...</p>
              ) : ventasServicios.length === 0 ? (
                <p className="text-center text-gray-500 py-6 text-sm">No hay piezas pendientes para este bus.</p>
              ) : (
                <div className="space-y-2">
                  {ventasServicios.map((v) => (
                    <div key={v.id} className="border rounded-lg p-3 flex justify-between items-center">
                      <div>
                        <p className="font-medium text-sm">{v.numero}</p>
                        <p className="text-xs text-gray-500">
                          {Array.isArray(v.items) ? v.items.map((it: any) => it.nombre).join(', ') : ''}
                        </p>
                      </div>
                      <p className="font-bold text-green-600">S/ {Number(v.total).toFixed(2)}</p>
                    </div>
                  ))}
                  <div className="flex justify-between pt-2 border-t font-bold">
                    <span>Total pendiente:</span>
                    <span>S/ {ventasServicios.reduce((sum, v) => sum + Number(v.total || 0), 0).toFixed(2)}</span>
                  </div>
                </div>
              )}

              <div className="mt-6 pt-4 border-t">
                {!mostrarHistorialCompras ? (
                  <button onClick={cargarHistorialCompras} className="text-sm text-blue-600 hover:underline">
                    Ver historial completo de compras de este bus
                  </button>
                ) : (
                  <>
                    <h3 className="font-semibold text-gray-700 mb-2">Historial de compras</h3>
                    {historialCompras.length === 0 ? (
                      <p className="text-center text-gray-500 py-4 text-sm">Sin compras registradas para este bus.</p>
                    ) : (
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {historialCompras.map((v) => (
                          <div key={v.id} className="border rounded-lg p-2 flex justify-between items-center text-sm">
                            <div>
                              <p className="font-medium">{v.numero} — {new Date(v.fecha).toLocaleDateString('es-PE')}</p>
                              <p className="text-xs text-gray-500">
                                {Array.isArray(v.items) ? v.items.map((it: any) => it.nombre).join(', ') : ''}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-green-600">S/ {Number(v.total).toFixed(2)}</p>
                              <span className={`text-xs ${v.cerrada ? 'text-gray-400' : 'text-orange-500 font-semibold'}`}>
                                {v.cerrada ? 'Cerrada' : 'Pendiente'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {mostrarCarritoServicios && busServicios?.padron && (
        <CarritoMantenimientoModal
          placaBus={busServicios.padron}
          onClose={() => setMostrarCarritoServicios(false)}
          onCompletada={() => {
            recargarVentasServicios();
            cargarBuses();
          }}
        />
      )}

      {/* Impresion en cola de las piezas cerradas del bus */}
      {colaImpresion.length > 0 && <TicketImprimible venta={colaImpresion[0]} />}

      {/* Modal: seleccionar rango de fechas para el historial impreso en la termica */}
      {mostrarSelectorHistorial && busHistorialTermico && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        >
          <div className="bg-white rounded-lg w-full max-w-sm p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Historial en Térmica</h3>
              <button onClick={() => setMostrarSelectorHistorial(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Bus {busHistorialTermico.padron || busHistorialTermico.numero} - {busHistorialTermico.placa}
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Desde</label>
                <input
                  type="date"
                  value={fechaDesdeHistorial}
                  onChange={(e) => setFechaDesdeHistorial(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Hasta</label>
                <input
                  type="date"
                  value={fechaHastaHistorial}
                  onChange={(e) => setFechaHastaHistorial(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
            </div>
            <button
              onClick={imprimirHistorialTermico}
              className="w-full mt-5 bg-indigo-600 hover:bg-indigo-700 text-white py-2 rounded-lg flex items-center justify-center gap-2"
            >
              <Printer size={16} />
              Imprimir
            </button>
          </div>
        </div>
      )}

      {historialParaImprimir && (
        <HistorialMantenimientosTicket
          bus={historialParaImprimir.bus}
          mantenimientos={historialParaImprimir.mantenimientos}
          desde={historialParaImprimir.desde}
          hasta={historialParaImprimir.hasta}
        />
      )}

      {imprimirRanking && (
        <RankingReparacionesTicket
          bus={imprimirRanking.bus}
          ranking={imprimirRanking.ranking}
          alertaActiva={imprimirRanking.alerta_activa}
          reparacionesUltimos15Dias={imprimirRanking.reparaciones_ultimos_15_dias}
          categoriaMasFrecuenteReciente={imprimirRanking.categoria_mas_frecuente_reciente}
        />
      )}

      {busParaPagarCredito && (
        <PagarCreditoModal
          bus={busParaPagarCredito}
          onClose={() => setBusParaPagarCredito(null)}
          onPagado={(padron, nuevoSaldo, nuevoSaldoProductos, nuevoSaldoServicios) => {
            setBuses(prev => prev.map(b => (b.padron === padron
              ? { ...b, credito_usado: nuevoSaldo, credito_usado_productos: nuevoSaldoProductos, credito_usado_servicios: nuevoSaldoServicios }
              : b)));
            setBusParaPagarCredito(null);
          }}
        />
      )}
    </div>
  );
};

export default BusesCompleto;